import {
    AdvancedListItemField,
    AdvancedListSearchItem,
    FieldDefinition,
    FieldDefinitionDataType,
    IdentityRef,
    ItemChoiceOption,
    ItemHyperlink,
    ReservedFieldKey
} from "@amzn/altar-sds-client";

export namespace AdvancedListImportFactory {
    export interface ParsedRow {
        values: AdvancedListItemField[];
        importId?: string;
        sheetFieldKeys?: string[];
    }

    export type RowAction =
        | { action: "create"; unmatchedImportId?: string }
        | { action: "update"; extraId: string };

    const RESERVED_FIELD_KEYS: ReadonlySet<string> = new Set<string>(
        Object.values(ReservedFieldKey)
    );

    // SDS stores whole numbers as 5.001; the export strips the marker.
    const WHOLE_NUMBER_MARKER_REGEX = /^-?\d+\.001$/;

    const MULTI_VALUE_SEPARATOR = "\u0000";

    export function isReservedFieldKey(fieldKey: string): boolean {
        return RESERVED_FIELD_KEYS.has(fieldKey);
    }

    export function findIdColumn(headers: Record<string, FieldDefinition>): string | undefined {
        const columns = Object.keys(headers ?? {});
        const reservedIdColumn = columns.find((column) => headers[column]?.fieldKey === ReservedFieldKey.ID);
        if (reservedIdColumn) return reservedIdColumn;
        return columns.find((column) => headers[column]?.dataType === FieldDefinitionDataType.sequence);
    }

    export function resolveIdFieldKey(headers: Record<string, FieldDefinition>): string | undefined {
        const idColumn = findIdColumn(headers);
        return idColumn ? headers[idColumn]?.fieldKey : undefined;
    }

    export function extractImportId(
        rowData: Record<string, any>,
        headers: Record<string, FieldDefinition>
    ): string | undefined {
        const idColumn = findIdColumn(headers);
        if (!idColumn) return undefined;
        const raw = rowData[idColumn];
        if (raw == null) return undefined;
        const trimmed = String(raw).trim();
        return trimmed.length ? trimmed : undefined;
    }

    export function extractSheetFieldKeys(headers: Record<string, FieldDefinition>): string[] {
        return Object.keys(headers ?? {})
            .map((column) => headers[column]?.fieldKey)
            .filter((fieldKey) => !!fieldKey);
    }

    export function buildSequenceToExtraIdMap(
        items: Pick<AdvancedListSearchItem, "values" | "entityExtraVersionRef">[],
        idFieldKey: string = ReservedFieldKey.ID
    ): Map<string, string> {
        const map = new Map<string, string>();
        for (const item of items ?? []) {
            const extraId = item?.entityExtraVersionRef?.entityExtraRef?.extraId;
            if (!extraId) continue;
            const idField = item?.values?.find((v) => v?.key === idFieldKey);
            if (idField?.value == null) continue;
            const key = String(idField.value).trim();
            if (!key.length) continue;
            map.set(key, extraId);
        }
        return map;
    }

    export function buildExtraIdToItemMap<T extends Pick<AdvancedListSearchItem, "values" | "entityExtraVersionRef">>(
        items: T[]
    ): Map<string, T> {
        const map = new Map<string, T>();
        for (const item of items ?? []) {
            const extraId = item?.entityExtraVersionRef?.entityExtraRef?.extraId;
            if (!extraId) continue;
            map.set(extraId, item);
        }
        return map;
    }

    // Unmatched IDs create: deleted serials are retired forever, so restores must still work.
    export function resolveRowAction(
        importId: string | undefined,
        idToExtraId: Map<string, string>
    ): RowAction {
        if (!importId) return { action: "create" };
        const extraId = idToExtraId.get(importId);
        if (extraId) return { action: "update", extraId };
        return { action: "create", unmatchedImportId: importId };
    }

    // Blank cell in a column the sheet has clears the field; an absent column never touches it.
    export function computeRemoveValues(
        fieldDefinitions: FieldDefinition[],
        values: AdvancedListItemField[],
        sheetFieldKeys: Iterable<string>
    ): string[] {
        const presentKeys = new Set((values ?? []).map((v) => v.key));
        const sheetKeys = toKeySet(sheetFieldKeys);
        return (fieldDefinitions ?? [])
            .filter((def) => sheetKeys.has(def.fieldKey))
            .filter((def) => !isReservedFieldKey(def.fieldKey))
            .filter((def) => def.dataType !== FieldDefinitionDataType.sequence)
            .filter((def) => !presentKeys.has(def.fieldKey))
            .map((def) => def.fieldKey);
    }

    // Skipping unchanged rows keeps their Updated Date / Updated By untouched.
    export function isRowUnchanged(
        parsedValues: AdvancedListItemField[],
        sheetFieldKeys: Iterable<string>,
        existingItem: Pick<AdvancedListSearchItem, "values">,
        fieldDefinitions: FieldDefinition[]
    ): boolean {
        if (!existingItem) return false;
        const definitionByKey = new Map<string, FieldDefinition>();
        (fieldDefinitions ?? []).forEach((def) => {
            if (def?.fieldKey) definitionByKey.set(def.fieldKey, def);
        });
        const parsedByKey = new Map<string, AdvancedListItemField>();
        (parsedValues ?? []).forEach((value) => {
            if (value?.key) parsedByKey.set(value.key, value);
        });
        const existingByKey = new Map<string, AdvancedListItemField>();
        (existingItem.values ?? []).forEach((value) => {
            if (value?.key) existingByKey.set(value.key, value);
        });

        for (const fieldKey of toKeySet(sheetFieldKeys)) {
            if (isReservedFieldKey(fieldKey)) continue;
            const parsed = parsedByKey.get(fieldKey);
            const existing = existingByKey.get(fieldKey);
            const dataType = String(
                definitionByKey.get(fieldKey)?.dataType ?? parsed?.type ?? existing?.type ?? ""
            );
            if (dataType === String(FieldDefinitionDataType.sequence)) continue;
            if (!parsed) {
                if (!isEmptyFieldValue(existing?.value, dataType)) return false;
                continue;
            }
            if (normalizeFieldValue(parsed.value, dataType) !== normalizeFieldValue(existing?.value, dataType)) {
                return false;
            }
        }
        return true;
    }

    export function toExportRows(rows: ParsedRow[]): Pick<AdvancedListSearchItem, "values">[] {
        return (rows ?? []).map((row) => {
            const values = [...(row?.values ?? [])];
            if (row?.importId) {
                values.unshift({
                    key: ReservedFieldKey.ID,
                    type: FieldDefinitionDataType.sequence,
                    value: row.importId
                });
            }
            return { values };
        });
    }

    // Merge instead of re-searching: the search index is populated asynchronously after the writes.
    // Creates and updates arrive separately because prevItems is only the table's current page.
    export function mergeImportedItems<T extends Pick<AdvancedListSearchItem, "entityExtraVersionRef">>(
        prevItems: readonly T[],
        updatedItems: readonly T[],
        createdItems: readonly T[]
    ): T[] {
        const existing = [...(prevItems ?? [])];
        const indexByExtraId = new Map<string, number>();
        existing.forEach((item, index) => {
            const extraId = item?.entityExtraVersionRef?.entityExtraRef?.extraId;
            if (extraId && !indexByExtraId.has(extraId)) indexByExtraId.set(extraId, index);
        });

        for (const updatedItem of updatedItems ?? []) {
            const extraId = updatedItem?.entityExtraVersionRef?.entityExtraRef?.extraId;
            if (!extraId) continue;
            const existingIndex = indexByExtraId.get(extraId);
            if (existingIndex !== undefined) existing[existingIndex] = updatedItem;
        }

        const addedItems: T[] = [];
        const addedIndexByExtraId = new Map<string, number>();
        for (const createdItem of createdItems ?? []) {
            const extraId = createdItem?.entityExtraVersionRef?.entityExtraRef?.extraId;
            if (!extraId) continue;
            const addedIndex = addedIndexByExtraId.get(extraId);
            if (addedIndex !== undefined) {
                addedItems[addedIndex] = createdItem;
                continue;
            }
            addedIndexByExtraId.set(extraId, addedItems.length);
            addedItems.push(createdItem);
        }

        return [...addedItems, ...existing];
    }

    export function chunk<T>(items: readonly T[], size: number): T[][] {
        const chunks: T[][] = [];
        const all = items ?? [];
        if (!all.length) return chunks;
        const chunkSize = size > 0 ? Math.floor(size) : all.length;
        for (let index = 0; index < all.length; index += chunkSize) {
            chunks.push(all.slice(index, index + chunkSize));
        }
        return chunks;
    }

    function toKeySet(fieldKeys: Iterable<string>): Set<string> {
        return fieldKeys ? new Set<string>(fieldKeys) : new Set<string>();
    }

    export function isEmptyFieldValue(value: any, dataType: string): boolean {
        if (value == null) return true;
        if (Array.isArray(value)) return value.length === 0;
        if (typeof value === "boolean") return false;
        if (typeof value === "object") {
            if (dataType === String(FieldDefinitionDataType.hyperlink)) {
                return !String((value as ItemHyperlink).href ?? "").trim().length;
            }
            if (dataType === String(FieldDefinitionDataType.IdentityRef)) {
                return !String((value as IdentityRef).id ?? "").trim().length;
            }
            return Object.keys(value).length === 0;
        }
        return !String(value).trim().length;
    }

    export function normalizeFieldValue(value: any, dataType: string): string {
        if (isEmptyFieldValue(value, dataType)) return "";
        switch (dataType) {
            case String(FieldDefinitionDataType.number):
                return normalizeNumber(value);
            case String(FieldDefinitionDataType.boolean):
                return String(!!value);
            case String(FieldDefinitionDataType.choice):
                return (value as ItemChoiceOption[])
                    .map((option) => String(option?.displayValue ?? "").trim())
                    .sort()
                    .join(MULTI_VALUE_SEPARATOR);
            case String(FieldDefinitionDataType.IdentityRef):
                return normalizeIdentityId((value as IdentityRef)?.id);
            case String(FieldDefinitionDataType.multiIdentityRef):
                return (value as IdentityRef[])
                    .map((identityRef) => normalizeIdentityId(identityRef?.id))
                    .sort()
                    .join(MULTI_VALUE_SEPARATOR);
            case String(FieldDefinitionDataType.hyperlink):
                // Only href survives the export, so comparing titles would flag every hyperlink row.
                return String((value as ItemHyperlink)?.href ?? "").trim();
            default:
                return String(value).trim();
        }
    }

    function normalizeIdentityId(id: any): string {
        return String(id ?? "").trim().toLowerCase();
    }

    function normalizeNumber(value: any): string {
        const raw = String(value).trim();
        const stripped = WHOLE_NUMBER_MARKER_REGEX.test(raw) ? raw.slice(0, -4) : raw;
        const num = Number(stripped);
        return Number.isNaN(num) ? stripped : String(num);
    }
}
